import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useNetworkList, useAttachRouterNetwork, networkCidr } from '@/api/opennebula';

/**
 * Attach one or more networks to a virtual router. Mirrors the network picker
 * of {@link RouterCreateDialog}: a checkbox list of networks not yet attached.
 * Attaches sequentially (the API takes one `network_id` per call).
 */
export function RouterAttachNetworkDialog({
  serviceId,
  routerId,
  attachedNetworkIds,
  open,
  onOpenChange,
}: {
  serviceId: string;
  routerId: string;
  /** Network ids already attached — excluded from the picker. */
  attachedNetworkIds: string[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { show } = useToast();
  const { data: networks = [] } = useNetworkList(open ? serviceId : undefined);
  const attach = useAttachRouterNetwork(serviceId, routerId);

  const [selected, setSelected] = useState<string[]>([]);
  const [pending, setPending] = useState(false);

  // The router payload identifies NICs by the numeric OpenNebula uid
  // (`nics[].network_id` = "229") while the network list keys on the UUID
  // `id` — match on either so already-attached networks never reappear.
  const available = useMemo(() => {
    const attached = new Set(attachedNetworkIds);
    return networks.filter((n) => !attached.has(String(n.uid)) && !attached.has(String(n.id)));
  }, [networks, attachedNetworkIds]);

  function close(next: boolean) {
    if (!next) setSelected([]);
    onOpenChange(next);
  }
  function toggle(id: string) {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  async function submit() {
    if (!selected.length) return;
    setPending(true);
    try {
      for (const id of selected) await attach.mutateAsync({ network_id: id });
      show({ tone: 'success', title: selected.length === 1 ? 'Network attached' : `${selected.length} networks attached` });
      close(false);
    } catch (e) {
      show({ tone: 'danger', title: (e as Error)?.message || 'Attach failed' });
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Attach network</DialogTitle>
          <DialogDescription>
            Networks attached to the same router can route traffic to each other.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {available.length === 0 ? (
            <Alert tone="info">All networks are already attached to this router.</Alert>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)', maxHeight: 260, overflowY: 'auto', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-2)' }}>
              {available.map((n) => {
                const c = networkCidr(n);
                return (
                  <label key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', padding: 'var(--vn-space-1) var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
                    <Checkbox checked={selected.includes(String(n.id))} onCheckedChange={() => toggle(String(n.id))} />
                    <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{n.name}</span>
                    {c && <code style={{ color: 'var(--vn-fg-muted)' }}>{c}</code>}
                  </label>
                );
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => close(false)} disabled={pending}>Cancel</Button>
          <Button disabled={!selected.length || pending} onClick={submit}>
            {pending ? 'Attaching…' : selected.length > 1 ? `Attach ${selected.length} networks` : 'Attach'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
